import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LogOut, Image, MessageSquare, Plus, Trash2, Edit2, 
  Mail, Eye, EyeOff, X, Check 
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cross SVG Component
const CrossIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m-4-8h8" />
  </svg>
);

export function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [sanctuaryItems, setSanctuaryItems] = useState([]);
  const [momentsItems, setMomentsItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    gallery_type: 'sanctuary',
    file: null,
  });
  const [uploading, setUploading] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editData, setEditData] = useState({ title: '', description: '' });
  
  // Message detail modal
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [sanctuaryRes, momentsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/galleries/sanctuary`),
        axios.get(`${API}/galleries/moments`),
        axios.get(`${API}/admin/messages`, { withCredentials: true }),
      ]);
      setSanctuaryItems(sanctuaryRes.data);
      setMomentsItems(momentsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    
    try {
      await axios.post(
        `${API}/admin/gallery/upload?title=${encodeURIComponent(uploadData.title)}&description=${encodeURIComponent(uploadData.description)}&gallery_type=${uploadData.gallery_type}`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      setUploadModalOpen(false);
      setUploadData({ title: '', description: '', gallery_type: 'sanctuary', file: null });
      fetchData();
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/admin/gallery/${editItem.id}`,
        editData,
        { withCredentials: true }
      );
      setEditModalOpen(false);
      setEditItem(null);
      fetchData();
    } catch (error) {
      console.error('Error updating:', error);
      alert('Update failed. Please try again.');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/admin/gallery/${itemId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const handleMarkRead = async (msgId) => {
    try {
      await axios.put(`${API}/admin/messages/${msgId}/read`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error marking read:', error);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/admin/messages/${msgId}`, { withCredentials: true });
      setSelectedMessage(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const openEditModal = (item) => {
    setEditItem(item);
    setEditData({ title: item.title, description: item.description });
    setEditModalOpen(true);
  };

  const getImageUrl = (item) => {
    if (item.image_url?.startsWith('http')) return item.image_url;
    return `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-700/30 border-t-emerald-700 rounded-full animate-spin" />
      </div>
    );
  }

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <CrossIcon className="w-8 h-8 text-emerald-700" />
              <div>
                <h1 className="font-cinzel text-lg text-slate-900">Admin Dashboard</h1>
                <p className="text-xs text-slate-500 font-lora">{user?.name}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors font-lora text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="sanctuary" className="space-y-6">
          <TabsList className="bg-white p-1 border border-slate-200">
            <TabsTrigger value="sanctuary" className="font-lora data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-sanctuary">
              <Image className="w-4 h-4 mr-2" />
              The Sanctuary
            </TabsTrigger>
            <TabsTrigger value="moments" className="font-lora data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid="tab-moments">
              <Image className="w-4 h-4 mr-2" />
              Moments
            </TabsTrigger>
            <TabsTrigger value="messages" className="font-lora data-[state=active]:bg-amber-600 data-[state=active]:text-white relative" data-testid="tab-messages">
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
              {unreadCount > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Sanctuary Gallery Tab */}
          <TabsContent value="sanctuary">
            <GalleryTab
              title="The Sanctuary"
              items={sanctuaryItems}
              onAdd={() => {
                setUploadData(prev => ({ ...prev, gallery_type: 'sanctuary' }));
                setUploadModalOpen(true);
              }}
              onEdit={openEditModal}
              onDelete={handleDelete}
              getImageUrl={getImageUrl}
              testIdPrefix="sanctuary"
            />
          </TabsContent>

          {/* Moments Gallery Tab */}
          <TabsContent value="moments">
            <GalleryTab
              title="Moments"
              items={momentsItems}
              onAdd={() => {
                setUploadData(prev => ({ ...prev, gallery_type: 'moments' }));
                setUploadModalOpen(true);
              }}
              onEdit={openEditModal}
              onDelete={handleDelete}
              getImageUrl={getImageUrl}
              testIdPrefix="moments"
            />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <h2 className="font-cinzel text-xl text-slate-900">Contact Messages</h2>
                <p className="font-lora text-sm text-slate-500 mt-1">{messages.length} total messages</p>
              </div>
              
              {messages.length === 0 ? (
                <div className="p-12 text-center">
                  <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="font-lora text-slate-500">No messages yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${!msg.is_read ? 'bg-amber-50/50' : ''}`}
                      onClick={() => {
                        setSelectedMessage(msg);
                        if (!msg.is_read) handleMarkRead(msg.id);
                      }}
                      data-testid={`message-${msg.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {!msg.is_read && (
                              <span className="w-2 h-2 bg-amber-500 rounded-full" />
                            )}
                            <h4 className="font-lora font-medium text-slate-900 truncate">{msg.name}</h4>
                          </div>
                          <p className="font-lora text-sm text-slate-500 truncate">{msg.email}</p>
                          <p className="font-lora text-sm text-slate-700 mt-1 truncate">{msg.subject}</p>
                        </div>
                        <span className="font-lora text-xs text-slate-400 ml-4 whitespace-nowrap">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cinzel">Add New Image</DialogTitle>
            <DialogDescription className="font-lora text-sm text-slate-500">
              Upload a new image to {uploadData.gallery_type === 'sanctuary' ? 'The Sanctuary' : 'Moments'} gallery.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="font-lora">Gallery</Label>
              <Select 
                value={uploadData.gallery_type} 
                onValueChange={(value) => setUploadData(prev => ({ ...prev, gallery_type: value }))}
              >
                <SelectTrigger className="font-lora" data-testid="upload-gallery-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sanctuary">The Sanctuary</SelectItem>
                  <SelectItem value="moments">Moments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="font-lora">Title</Label>
              <Input
                value={uploadData.title}
                onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                required
                className="font-lora"
                placeholder="Enter image title"
                data-testid="upload-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-lora">Description</Label>
              <Textarea
                value={uploadData.description}
                onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                className="font-lora resize-none"
                rows={3}
                placeholder="Describe the image..."
                data-testid="upload-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-lora">Image File</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                required
                className="font-lora"
                data-testid="upload-file-input"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setUploadModalOpen(false)}
                className="px-4 py-2 font-lora text-sm text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="btn-primary text-sm disabled:opacity-50"
                data-testid="upload-submit-btn"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cinzel">Edit Image</DialogTitle>
            <DialogDescription className="font-lora text-sm text-slate-500">
              Update the title and description of this image.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="font-lora">Title</Label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                required
                className="font-lora"
                data-testid="edit-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-lora">Description</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                className="font-lora resize-none"
                rows={4}
                data-testid="edit-description-input"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 font-lora text-sm text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary text-sm" data-testid="edit-submit-btn">
                Save Changes
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message Detail Modal */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cinzel">{selectedMessage?.subject}</DialogTitle>
            <DialogDescription className="font-lora text-sm text-slate-500">
              From: {selectedMessage?.name} ({selectedMessage?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <p className="font-lora text-slate-700 whitespace-pre-wrap leading-relaxed">
              {selectedMessage?.message}
            </p>
            <p className="font-lora text-xs text-slate-400 mt-4">
              Received: {selectedMessage && new Date(selectedMessage.created_at).toLocaleString()}
            </p>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={() => handleDeleteMessage(selectedMessage?.id)}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-sm font-lora text-sm flex items-center gap-2"
                data-testid="message-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
              <button
                onClick={() => setSelectedMessage(null)}
                className="btn-primary text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Gallery Tab Component
function GalleryTab({ title, items, onAdd, onEdit, onDelete, getImageUrl, testIdPrefix }) {
  return (
    <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h2 className="font-cinzel text-xl text-slate-900">{title}</h2>
          <p className="font-lora text-sm text-slate-500 mt-1">{items.length} images</p>
        </div>
        <button
          onClick={onAdd}
          className="btn-primary text-sm inline-flex items-center gap-2"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus className="w-4 h-4" />
          Add Image
        </button>
      </div>
      
      {items.length === 0 ? (
        <div className="p-12 text-center">
          <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="font-lora text-slate-500">No images yet. Add your first one!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
          {items.map((item) => (
            <div key={item.id} className="border border-slate-200 rounded-sm overflow-hidden group" data-testid={`${testIdPrefix}-item-${item.id}`}>
              <div className="relative h-48">
                <img
                  src={getImageUrl(item)}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => onEdit(item)}
                    className="p-2 bg-white rounded-full text-slate-700 hover:bg-amber-100"
                    data-testid={`${testIdPrefix}-edit-${item.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="p-2 bg-white rounded-full text-red-600 hover:bg-red-100"
                    data-testid={`${testIdPrefix}-delete-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h4 className="font-cinzel text-sm text-slate-900 truncate">{item.title}</h4>
                <p className="font-lora text-xs text-slate-500 truncate mt-1">{item.description || 'No description'}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
